import React, { useState } from 'react';
import { format } from 'date-fns';
import { reactToMessage, deleteMessage } from '../services/api';

const EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '🔥'];

// ─── Bulk broadcast card (admin side) ────────────────────────────────────────
function BulkCard({ message }) {
  const time = message.createdAt ? format(new Date(message.createdAt), 'HH:mm') : '';
  return (
    <div style={bc.card}>
      <div style={bc.accentBar} />
      {message.fileUrl && (
        <img
          src={message.fileUrl}
          alt="broadcast"
          style={bc.img}
          onClick={() => window.open(message.fileUrl, '_blank')}
        />
      )}
      <div style={bc.body}>
        <div style={bc.titleRow}>
          <span style={bc.icon}>📣</span>
          <span style={bc.title}>{message.bulkTitle || 'Announcement'}</span>
        </div>
        <div style={bc.divider} />
        <p style={bc.desc}>{message.content}</p>
        <div style={bc.footer}>
          <span style={bc.time}>{time}</span>
          
          {message.status === 'read'
            ? <span style={{ color: '#f5a623', fontSize: '11px' }}>✓✓</span>
            : <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px' }}>✓</span>}
        </div>
      </div>
    </div>
  );
}

const bc = {
  card: {
    background: 'linear-gradient(145deg,#0d1a2a,#0a1520)',
    border: '1px solid rgba(245,166,35,0.2)',
    borderRadius: '16px',
    overflow: 'hidden',
    maxWidth: '320px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
  },
  accentBar: {
    height: '3px',
    background: 'linear-gradient(90deg,#f5a623,#00aaff,#7b61ff)',
  },
  img: {
    width: '100%',
    maxHeight: '160px',
    objectFit: 'cover',
    display: 'block',
    cursor: 'pointer',
  },
  body: { padding: '12px 14px 10px' },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '7px',
    marginBottom: '8px',
  },
  icon: { fontSize: '16px', lineHeight: 1 },
  title: {
    fontSize: '14px',
    fontWeight: '800',
    color: '#e2e8f0',
    fontFamily: "'Rajdhani', sans-serif",
    letterSpacing: '0.3px',
  },
  divider: {
    height: '1px',
    background: 'linear-gradient(90deg,rgba(245,166,35,0.3),transparent)',
    marginBottom: '8px',
  },
  desc: {
    margin: '0 0 10px',
    fontSize: '13px',
    color: 'rgba(255,255,255,0.55)',
    lineHeight: '1.5',
    wordBreak: 'break-word',
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    justifyContent: 'flex-end',
  },
  time: { fontSize: '10px', color: 'rgba(255,255,255,0.25)', marginRight: 'auto' },
  label: {
    fontSize: '9px',
    color: 'rgba(245,166,35,0.5)',
    fontWeight: '700',
    letterSpacing: '1px',
  },
};

// ─── Main MessageBubble ───────────────────────────────────────────────────────
export default function MessageBubble({ message, onReply }) {
  const [showMenu, setShowMenu] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const isAdmin = message.senderType === 'admin';
  const isBulk  = message.type === 'bulk';

  if (message.isDeletedForEveryone) {
    return (
      <div style={{ ...styles.wrap, justifyContent: isAdmin ? 'flex-end' : 'flex-start' }}>
        <div style={styles.deleted}>🚫 Message deleted</div>
      </div>
    );
  }

  const handleReact = async (emoji) => {
    try { await reactToMessage(message._id, emoji); setShowEmojiPicker(false); }
    catch (err) { console.error(err); }
  };

  const handleDelete = async (forEveryone) => {
    try { await deleteMessage(message._id, forEveryone); setShowMenu(false); }
    catch (err) { console.error(err); }
  };

  const statusIcon = () => {
    if (!isAdmin) return null;
    if (message.status === 'read')      return <span style={{ color: '#f5a623' }}>✓✓</span>;
    if (message.status === 'delivered') return <span style={{ color: 'rgba(255,255,255,0.4)' }}>✓✓</span>;
    return <span style={{ color: 'rgba(255,255,255,0.4)' }}>✓</span>;
  };

  // ── Bulk card — full width, no hover menu ──────────────────────────────────
  if (isBulk) {
    return (
      <div style={{ ...styles.wrap, alignItems: 'flex-end', paddingRight: '16px' }}>
        <BulkCard message={message} />
        {message.reactions?.length > 0 && (
          <div style={{ ...styles.reactions, alignSelf: 'flex-end' }}>
            {message.reactions.map((r, i) => (
              <span key={i} style={styles.reaction}>{r.emoji}</span>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── Regular message bubble ─────────────────────────────────────────────────
  return (
    <div
      style={{ ...styles.wrap, justifyContent: isAdmin ? 'flex-end' : 'flex-start' }}
      onMouseEnter={() => setShowMenu(true)}
      onMouseLeave={() => { setShowMenu(false); setShowEmojiPicker(false); }}
    >
      {message.replyTo && (
        <div style={{ ...styles.replyPreview, alignSelf: isAdmin ? 'flex-end' : 'flex-start' }}>
          ↩ {message.replyTo.content || `📎 ${message.replyTo.type}`}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px', flexDirection: isAdmin ? 'row-reverse' : 'row' }}>
        <div style={{
          ...styles.bubble,
          background: isAdmin ? 'linear-gradient(135deg,#00432e,#003d38)' : 'rgba(255,255,255,0.06)',
          borderRadius: isAdmin ? '18px 4px 18px 18px' : '4px 18px 18px 18px',
          border: isAdmin ? '1px solid rgba(245,166,35,0.2)' : '1px solid rgba(255,255,255,0.08)',
        }}>
          {message.type === 'image' && (
            <div>
              <img src={message.fileUrl} alt="media" style={styles.imgMsg} onClick={() => window.open(message.fileUrl, '_blank')} />
              {message.content && <p style={{ ...styles.text, marginTop: '6px', fontSize: '13px' }}>{message.content}</p>}
            </div>
          )}
          {message.type === 'video' && (
            <div>
              <video src={message.fileUrl} controls style={styles.imgMsg} />
              {message.content && <p style={{ ...styles.text, marginTop: '6px', fontSize: '13px' }}>{message.content}</p>}
            </div>
          )}
          {message.type === 'audio' && (
            <div>
              <audio src={message.fileUrl} controls style={{ maxWidth: '220px' }} />
              {message.content && <p style={{ ...styles.text, marginTop: '6px', fontSize: '13px' }}>{message.content}</p>}
            </div>
          )}
          {message.type === 'document' && (
            <div>
              <a href={message.fileUrl} target="_blank" rel="noreferrer" style={styles.docLink}>
                📄 {message.fileName || 'Document'}
              </a>
              {message.content && <p style={{ ...styles.text, marginTop: '6px', fontSize: '13px' }}>{message.content}</p>}
            </div>
          )}
          {message.type === 'text' && (
            <p style={styles.text}>{message.content}</p>
          )}
          <div style={styles.footer}>
            <span style={styles.time}>
              {message.createdAt ? format(new Date(message.createdAt), 'HH:mm') : ''}
            </span>
            {statusIcon()}
          </div>
        </div>

        {showMenu && (
          <div style={styles.menuWrap}>
            <button style={styles.menuBtn} onClick={() => setShowEmojiPicker(!showEmojiPicker)}>😊</button>
            <button style={styles.menuBtn} onClick={() => onReply(message)}>↩</button>
            {isAdmin && <button style={styles.menuBtn} onClick={() => handleDelete(true)}>🗑</button>}
          </div>
        )}
      </div>

      {showEmojiPicker && (
        <div style={{ ...styles.emojiPicker, alignSelf: isAdmin ? 'flex-end' : 'flex-start' }}>
          {EMOJIS.map((e) => (
            <button key={e} style={styles.emojiBtn} onClick={() => handleReact(e)}>{e}</button>
          ))}
        </div>
      )}

      {message.reactions?.length > 0 && (
        <div style={{ ...styles.reactions, alignSelf: isAdmin ? 'flex-end' : 'flex-start' }}>
          {message.reactions.map((r, i) => (
            <span key={i} style={styles.reaction}>{r.emoji}</span>
          ))}
        </div>
      )}
    </div>
  );
}

const styles = {
  wrap: {
    display: 'flex', flexDirection: 'column',
    gap: '2px', padding: '2px 16px', position: 'relative',
  },
  bubble: { maxWidth: '420px', padding: '10px 14px', cursor: 'default' },
  text: {
    margin: 0, color: '#e2e8f0', fontSize: '14px',
    lineHeight: '1.5', wordBreak: 'break-word', fontFamily: "'Exo 2', sans-serif",
  },
  footer: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '4px', marginTop: '4px' },
  time: { fontSize: '10px', color: 'rgba(255,255,255,0.3)' },
  imgMsg: { maxWidth: '280px', maxHeight: '200px', borderRadius: '10px', cursor: 'pointer', display: 'block' },
  docLink: { color: '#f5a623', fontSize: '13px', textDecoration: 'none', display: 'block', padding: '4px 0' },
  replyPreview: {
    background: 'rgba(245,166,35,0.05)', border: '1px solid rgba(245,166,35,0.15)',
    borderRadius: '8px', padding: '6px 12px', fontSize: '12px',
    color: 'rgba(255,255,255,0.4)', maxWidth: '300px',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  deleted: { fontSize: '12px', color: 'rgba(255,255,255,0.25)', fontStyle: 'italic', padding: '6px 12px' },
  menuWrap: {
    display: 'flex', gap: '4px', background: '#1a2535',
    border: '1px solid rgba(245,166,35,0.15)', borderRadius: '10px', padding: '4px',
  },
  menuBtn: { background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', padding: '4px 6px', borderRadius: '6px' },
  emojiPicker: {
    display: 'flex', gap: '4px', background: '#1a2535',
    border: '1px solid rgba(245,166,35,0.15)', borderRadius: '10px', padding: '6px 10px',
  },
  emojiBtn: { background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', padding: '2px' },
  reactions: { display: 'flex', gap: '4px', flexWrap: 'wrap' },
  reaction: {
    background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '10px', padding: '2px 6px', fontSize: '13px',
  },
};
